import * as PIXI from "pixi.js";

/**
 * Draw the game's lettering at the size it is SHOWN at (2026-10-02).
 *
 * ── The problem ──
 *
 * The jackpot plates, the popup words and numbers, the counters — every live
 * PIXI.Text in this game's own lettering (the `dialogs` look, NotoSans-Black,
 * and the coin look, Kanit) — is rasterised at a fixed resolution (2, or the
 * renderer's) at its STYLE size, then shrunk by the spines it rides and the
 * scene scale: 2.5–5x on screen. A texture minified that much without
 * mipmaps samples a few texels per screen pixel, so the thick outlines came
 * out stair-stepped (Vladimir: "awfully pixelated", "all must be smooth").
 *
 * Mipmaps are NOT the fix for these: they redraw (a counter counts every
 * frame), and mipmaps on a live PIXI.Text gave black flashes (assetsSlice.js
 * "Global mipmap causes black background flashes on PIXI.Text"). The cached
 * coin labels, drawn once, do take mipmaps (textLabelCache.js).
 *
 * ── The fix ──
 *
 * Before each render, the Text's resolution is set from its WORLD scale —
 * texture pixels per screen pixel ≈ OVERSAMPLE — so it is never shrunk by
 * much. Every PIXI.Text the game draws (Vladimir: "all must be smooth"); a
 * text at its natural size just gets a little over the renderer's density.
 * A text can opt out with `keepResolution = true`. The label cache's Texts
 * are never rendered themselves (Sprites wear their textures), so they keep
 * their own 2x + mipmaps.
 *
 * A change of resolution re-rasterises, so it moves in quarter-octave steps:
 * UP at once (never magnified and soft), DOWN only once the wanted value has
 * held for SETTLE_FRAMES (a popup's grow-in from 0 or a bounce does not
 * redraw it every frame — it settles once, at rest).
 *
 * ── Spread, not all in one frame ──
 *
 * A big resize changes the scale of EVERY text at once, and a redraw is a
 * canvas raster plus a GPU upload. Only a VISIBLE text that grows needs it
 * this frame. A hidden one (worldAlpha ~0: a parked dialog at 0.001 still
 * renders) is not seen, and a shrinking one is merely sharper than needed
 * for a moment — so those take a budget of DEFERRED_PER_FRAME redraws per
 * frame and catch up over the next frames. A hidden text that becomes
 * visible first is visible from then on, and redraws at once.
 */

// Texture pixels per screen pixel. A little over 1 so linear filtering only
// ever shrinks by up to ~1.2x — smooth, and still sharp.
const OVERSAMPLE = 1.25;
const MIN_RESOLUTION = 0.5;
// const MAX_RESOLUTION = 4;
// 3 (2026-10-02, Vladimir: no enormous textures) — covers a text up to 1.2x
// magnified on a 2x screen; past that it is drawn at 3 and stretched a little.
const MAX_RESOLUTION = 3;
// Frames the wanted resolution must hold before the text is redrawn at it.
const SETTLE_FRAMES = 6;
// Quarter-octave steps (~19%): small enough to stay sharp, large enough not
// to redraw on every small scale change.
const STEPS_PER_OCTAVE = 4;

// Redraws per frame for hidden texts and for shrinking ones.
const DEFERRED_PER_FRAME = 4;
// At or below this world alpha a text counts as hidden.
const HIDDEN_ALPHA = 0.01;

let budgetFrame = -1;
let budgetUsed = 0;
// Keyed on the renderer's own frame counter (textureGC.count: +1 per frame
// drawn to the screen, in every GC mode) — the game renders on @pixi/react's
// ticker, and Ticker.shared stops while it has no listeners.
const takeDeferredBudget = (renderer) => {
  const frame = renderer?.textureGC?.count ?? -1;
  if (frame !== budgetFrame) {
    budgetFrame = frame;
    budgetUsed = 0;
  }
  if (budgetUsed >= DEFERRED_PER_FRAME) return false;
  budgetUsed += 1;
  return true;
};

const quantize = (value) => {
  const clamped = Math.min(MAX_RESOLUTION, Math.max(MIN_RESOLUTION, value));
  return 2 ** (Math.round(Math.log2(clamped) * STEPS_PER_OCTAVE) / STEPS_PER_OCTAVE);
};

let installed = false;

/** Patch PIXI.Text once. Safe to call more than once. */
export const installOnScreenTextResolution = () => {
  if (installed) return;
  installed = true;

  const originalRender = PIXI.Text.prototype._render;
  PIXI.Text.prototype._render = function renderAtOnScreenResolution(renderer) {
    if (!this.keepResolution) {
      const wt = this.worldTransform;
      const scale = Math.max(Math.hypot(wt.a, wt.b), Math.hypot(wt.c, wt.d));
      if (scale > 0) {
        const want = quantize(scale * (renderer.resolution || 1) * OVERSAMPLE);
        // We own it from here — PIXI's auto resolution would put the
        // renderer's back on every frame.
        this._autoResolution = false;
        const visible = this.worldAlpha > HIDDEN_ALPHA;
        if (want === this._resolution) {
          this._wantedResolution = null;
        } else if (want > this._resolution) {
          // Growing (a magnified text, or a popup's grow-in passing its
          // current density): at once when visible — never shown magnified
          // and soft. Hidden: on the deferred budget.
          if (visible || takeDeferredBudget(renderer)) {
            this._resolution = want;
            this.dirty = true;
            this._wantedResolution = null;
          }
        } else if (this._wantedResolution === want) {
          this._wantedFrames = (this._wantedFrames || 0) + 1;
          if (this._wantedFrames >= SETTLE_FRAMES && takeDeferredBudget(renderer)) {
            this._resolution = want;
            this.dirty = true;
            this._wantedResolution = null;
          }
        } else {
          // Shrinking: only once it has held (a popup starting from scale 0
          // keeps its density through the grow-in, then settles once).
          this._wantedResolution = want;
          this._wantedFrames = 1;
        }
      }
    }
    originalRender.call(this, renderer);
  };
};
